import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Building2, Check } from 'lucide-react';
import { useWorkspaces } from './WorkspaceProvider';

/**
 * Workspace Switcher — lets users who belong to multiple workspaces switch
 * instantly. Hidden when the user belongs to zero or one workspace.
 * Portals the dropdown to document.body (dropdown-portal) so it is never
 * trapped by header stacking contexts.
 */
export default function WorkspaceSwitcher() {
  const { memberships, activeWorkspace, switchWorkspace } = useWorkspaces();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const total = memberships.length;
  if (total <= 1) return null;

  const displayName = activeWorkspace?.orgName || activeWorkspace?.name || 'Workspace';

  useEffect(() => {
    if (!open) return;
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 8, left: Math.max(8, rect.right - 280) });
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer text-left"
        title="Switch workspace"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="h-6 w-6 rounded-md bg-brand/10 text-brand flex items-center justify-center shrink-0">
          <Building2 className="h-3.5 w-3.5" />
        </div>
        <span className="hidden sm:block text-xs font-bold text-slate-700 max-w-[140px] truncate">{displayName}</span>
        <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && pos && ReactDOM.createPortal(
        <div
          ref={panelRef}
          role="menu"
          className="dropdown-portal w-72 max-w-[85vw] bg-white rounded-2xl shadow-2xl border border-slate-200 py-2"
          style={{ top: pos.top, left: pos.left, zIndex: 'var(--z-dropdown)' }}
        >
          <div className="px-4 py-2 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
            Switch Workspace
          </div>
          <div className="py-1 max-h-80 overflow-y-auto">
            {memberships.map(m => {
              const active = m.workspaceId === activeWorkspace?.workspaceId;
              return (
                <button
                  key={m.workspaceId}
                  onClick={() => { setOpen(false); switchWorkspace(m.workspaceId); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all cursor-pointer ${active ? 'bg-brand-active' : 'hover:bg-slate-50'}`}
                >
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-brand text-white' : 'bg-slate-100 text-slate-500'}`}>
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-black text-slate-800 truncate">{m.orgName || m.name}</p>
                    <p className="text-[10px] text-slate-400 truncate">
                      {m.slug}
                      {m.role ? ` · ${m.role}` : ''}
                    </p>
                  </div>
                  {active && <Check className="h-4 w-4 text-brand shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
